/* common utils for item sharing authoring */
import { stringKeyToTuple, tupleToStringKey } from 'js/lib/stringKeyTuple';

import type AuthoringCourseBranch from 'bundles/author-branches/models/AuthoringCourseBranch';
import type AuthoringCourse from 'bundles/author-common/models/AuthoringCourse';
import type { Item } from 'bundles/author-common/types/Item';
import { ContextStatusStates } from 'bundles/author-course-level/constants/ContextStatusStates';
import { WriteAccessStates } from 'bundles/authoring/common/constants';
import type { WriteAccessState } from 'bundles/authoring/common/constants';
import { authoringCourseContextTypes } from 'bundles/authoring/common/constants/authoringCourseContexts';
import type {
  AuthoringCourseContext,
  AuthoringCourseContextInformation,
} from 'bundles/authoring/common/types/authoringCourseContexts';
import type {
  AtomRelationV2,
  ItemContentRelations,
  ItemContentRelationsV2,
  RelatedUsage,
  RelatedUsageV2,
  UsagesByCourseAndBranchId,
} from 'bundles/authoring/common/types/itemSharing';

export const getAuthoringItemContentId = (branchId: string, itemId: string, atomId?: string): string => {
  return atomId || tupleToStringKey([branchId, itemId]);
};

export const getUnversionedAtomId = (atomId: string): string => {
  return atomId.split('@')[0];
};

/**
 *
 * @param {Array} itemContentRelations - list of content relations for an item
 * @return {boolean} whether the item is linked
 */
export const isLinkedItem = (itemContentRelations: ItemContentRelations, branchId: string, itemId: string): boolean => {
  const branchItemId = getAuthoringItemContentId(branchId, itemId);

  return (
    !!itemContentRelations &&
    !!itemContentRelations.branchRelationMap &&
    (Object.keys(itemContentRelations.branchRelationMap).length > 1 || // more than 1 relation means item is linked
      !itemContentRelations.branchRelationMap[branchItemId]) // only 1 relation exists and it's not itself
  );
};

// whether an item is frozen as determined by its writeAccessState
export const isItemFrozen = (writeAccessState: string | WriteAccessState | null | undefined = '') =>
  writeAccessState === WriteAccessStates.ATOM_VERSION_NOT_AT_LATEST;

// whether an item is read-only due to insufficient permissions as determined by its writeAccessState
export const userHasInsufficientPermissions = (writeAccessState: string | WriteAccessState | null | undefined = '') =>
  writeAccessState === WriteAccessStates.INSUFFICIENT_PERMISSIONS;

/**
 *
 * @param {Array} item - the item to check if linking is supported
 * @return {boolean} whether the item is a type that we support linking for
 */
export const itemTypeSupportsLinking = (item: Item) => {
  return item.content.typeName !== 'teammateReview' && item.content.typeName !== 'wiseFlow';
};

/**
 * @param {Array} authoringCourseBranchItemId - branchId~itemId OR authoringBranch!~branchId~itemId
 * @return {string} branchId
 */
export const getBranchIdFromBranchItemId = (authoringCourseBranchItemId: string): string => {
  return stringKeyToTuple(authoringCourseBranchItemId)[0];
};

/**
 *
 * @param {Array} authoringCourseBranchItemId - branchId~itemId OR authoringBranch!~branchId~itemId
 * @return {string} itemId
 */
export const getItemIdFromBranchItemId = (authoringCourseBranchItemId: string): string => {
  return stringKeyToTuple(authoringCourseBranchItemId)[1];
};

export const shouldAddCurrentUsage = (
  itemContentRelations: ItemContentRelations,
  branchId: string,
  itemId: string
): boolean => {
  // branchRelationMap does not contain current branch-item if it's unlaunched,
  // and we still need to show this usage in the UI
  return !itemContentRelations.branchRelationMap[tupleToStringKey([branchId, itemId])];
};

export const shouldShowUpdateAll = (relatedUsages: Array<RelatedUsage>, hasNewGradedness = false): boolean => {
  // only show this when there is one more usage, other than the current one.
  return !hasNewGradedness && relatedUsages.length > 1;
};

export const shouldShowUpdateHereOnly = (hasNewGradedness = false): boolean => {
  return !hasNewGradedness;
};

export const shouldShowUnlink = (): boolean => {
  // always show, unless for graded items when we hide all options
  return true;
};

export const calculateNumUsages = (
  itemContentRelations: ItemContentRelations,
  branchId: string,
  itemId: string
): number => {
  if (!itemContentRelations || !itemContentRelations.branchRelationMap) {
    return 0;
  }
  const branchRelationMapLength = Object.keys(itemContentRelations.branchRelationMap).length;
  if (itemContentRelations.branchRelationMap[tupleToStringKey([branchId, itemId])]) {
    return branchRelationMapLength;
  } else {
    return branchRelationMapLength + 1;
  }
};

export const getOnlyLatestUsages = (itemContentRelations: ItemContentRelations): ItemContentRelations => {
  const newBranchRelationMap = {};
  Object.keys(itemContentRelations.branchRelationMap).forEach((branchItemId: string) => {
    const relation = itemContentRelations.branchRelationMap[branchItemId];
    if (!relation.atomVersion) {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      newBranchRelationMap[branchItemId] = relation;
    }
  });
  return {
    atomId: itemContentRelations.atomId,
    branchRelationMap: newBranchRelationMap,
  };
};

// The context id is the groupId for a session and authoringCourseBranchId for public and private branches.
export const getContextId = (context?: AuthoringCourseContext['definition'] | AuthoringCourseContextInformation) => {
  if (!context) {
    return '';
  }
  return 'groupId' in context ? context.groupId : context.authoringCourseBranchId;
};

export const isCurrentContext = (context: AuthoringCourseContext, currentContextId: string) => {
  return getContextId(context.definition) === currentContextId;
};

export const isContextArchived = (context: AuthoringCourseContext) => {
  return (
    context.definition.status === ContextStatusStates.ENDED ||
    context.definition.status === ContextStatusStates.ARCHIVED
  );
};

// A live context (not the status live) is any context that isn't ended or archived.
export const isCurrentOrLiveContext = (context: AuthoringCourseContext, currentContextId: string) => {
  return !isContextArchived(context) || isCurrentContext(context, currentContextId);
};

const hasLiveContextInBranch = (relatedBranch: RelatedUsageV2[], currentContextId: string) => {
  return relatedBranch.some((context) => isCurrentOrLiveContext(context, currentContextId));
};

export const hasLiveContextsInCourse = (
  usagesByCourseAndBranchId: UsagesByCourseAndBranchId,
  courseId: string,
  currentContextId: string
) => {
  const relatedBranches = Object.values(usagesByCourseAndBranchId[courseId]);

  for (const relatedBranch of relatedBranches) {
    if (hasLiveContextInBranch(relatedBranch, currentContextId)) {
      return true;
    }
  }
  return false;
};

export const getContextUsages = (usagesByCourseAndBranchId: UsagesByCourseAndBranchId, currentContextId: string) => {
  // Course with live contexts.
  const coursesWithLiveUsages: UsagesByCourseAndBranchId = {};
  // Course with no live contexts.
  const coursesWithOnlyArchivedUsages: UsagesByCourseAndBranchId = {};
  // Archived contexts in course with live contexts.
  const coursesWithArchivedUsages: UsagesByCourseAndBranchId = {};

  Object.keys(usagesByCourseAndBranchId).forEach((courseId) => {
    const relatedBranches = usagesByCourseAndBranchId[courseId];

    const hasLiveContextInCourse = hasLiveContextsInCourse(usagesByCourseAndBranchId, courseId, currentContextId);
    if (!hasLiveContextInCourse) {
      coursesWithOnlyArchivedUsages[courseId] = relatedBranches;
      return;
    }

    Object.keys(relatedBranches).forEach((branchId) => {
      const relatedBranch = relatedBranches[branchId];
      if (hasLiveContextInBranch(relatedBranch, currentContextId)) {
        coursesWithLiveUsages[courseId] = coursesWithLiveUsages[courseId] ?? {};
        coursesWithLiveUsages[courseId][branchId] = relatedBranch;
      } else {
        coursesWithArchivedUsages[courseId] = coursesWithArchivedUsages[courseId] ?? {};
        coursesWithArchivedUsages[courseId][branchId] = relatedBranch;
      }
    });
  });

  return { coursesWithLiveUsages, coursesWithOnlyArchivedUsages, coursesWithArchivedUsages };
};

export const getOtherContextsCount = (
  usagesByCourseAndBranchId: UsagesByCourseAndBranchId,
  currentContextId: string,
  isPublish: boolean
) => {
  let count = 0;

  Object.values(usagesByCourseAndBranchId).forEach((relatedBranches) => {
    count += Object.values(relatedBranches).reduce((prev, relatedUsage) => {
      if (isPublish) {
        return prev + relatedUsage.filter((context) => isCurrentOrLiveContext(context, currentContextId)).length;
      }
      return prev + relatedUsage.length;
    }, 0);
  });

  return count;
};

export const getContextsInBranchesCount = (relatedBranches: UsagesByCourseAndBranchId['courseId']) => {
  return Object.values(relatedBranches).reduce((prev, relatedUsage) => prev + relatedUsage.length, 0);
};

export const getEnrollmentOrMembersCount = (usage: RelatedUsageV2) => {
  const { typeName, definition } = usage;
  const { PRIVATE_BRANCH, PUBLIC_BRANCH } = authoringCourseContextTypes;

  if (typeName === PRIVATE_BRANCH || typeName === PUBLIC_BRANCH) {
    return definition.enrollmentCount ?? 0;
  }

  return definition.enrollmentCount ? definition.enrollmentCount : definition.confirmedMembersCount ?? 0;
};

const sortContexts = (relatedUsage: RelatedUsageV2[], currentContextId: string) => {
  const orderedContexts: { [key: string]: RelatedUsageV2[] } = {
    [ContextStatusStates.LIVE]: [],
    [ContextStatusStates.UPCOMING]: [],
    [ContextStatusStates.NEW]: [],
    [ContextStatusStates.ARCHIVED]: [],
  };

  relatedUsage.forEach((context) => {
    if (isCurrentContext(context, currentContextId)) {
      orderedContexts[ContextStatusStates.LIVE].unshift(context);
      return;
    }
    switch (context.definition.status) {
      case ContextStatusStates.LIVE:
      case ContextStatusStates.RUNNING:
      case ContextStatusStates.OPEN:
        orderedContexts[ContextStatusStates.LIVE].push(context);
        break;
      case ContextStatusStates.UPCOMING:
      case ContextStatusStates.PENDING:
        orderedContexts[ContextStatusStates.UPCOMING].push(context);
        break;
      case ContextStatusStates.NEW:
      case ContextStatusStates.CREATING:
        orderedContexts[ContextStatusStates.NEW].push(context);
        break;
      case ContextStatusStates.ARCHIVED:
      case ContextStatusStates.ENDED:
        orderedContexts[ContextStatusStates.ARCHIVED].push(context);
        break;
      default:
        break;
    }
  });

  const sortContextsInUsage = (a: RelatedUsageV2, b: RelatedUsageV2) => {
    if (isCurrentContext(b, currentContextId)) {
      return -1;
    } else if (b.definition.startsAt) {
      return b.definition.startsAt > (a.definition.startsAt || a.definition.createdAt || 0) ? 1 : -1;
    } else if (b.definition.createdAt) {
      return b.definition.createdAt > (a.definition.startsAt || a.definition.createdAt || 0) ? 1 : -1;
    }
    return 0;
  };

  return [
    ...orderedContexts[ContextStatusStates.LIVE].sort(sortContextsInUsage),
    ...orderedContexts[ContextStatusStates.UPCOMING].sort(sortContextsInUsage),
    ...orderedContexts[ContextStatusStates.NEW].sort(sortContextsInUsage),
    ...orderedContexts[ContextStatusStates.ARCHIVED].sort(sortContextsInUsage),
  ];
};

export const buildRelatedUsagesV2 = ({
  relatedBranches,
  currentCourseId,
  currentContextId,
  excludeArchivedContexts,
}: {
  relatedBranches: ItemContentRelationsV2['branchRelationMap'];
  currentCourseId: string;
  currentContextId: string;
  excludeArchivedContexts: boolean;
}) => {
  const usagesByCourseAndBranchId: UsagesByCourseAndBranchId = {};

  const usagesByBranchId: UsagesByCourseAndBranchId['courseId'] = {};

  // Store the currentBranchId for the context to make sure we can reorder it to be at the top.
  let branchIdWithCurrentContext = '';

  let enrollmentsCount = 0;
  let sessionsCount = 0;

  Object.values(relatedBranches).forEach((atomRelation: AtomRelationV2) => {
    Object.values(atomRelation.courseContext).forEach((context: AuthoringCourseContext) => {
      if (!isCurrentContext(context, currentContextId) && isContextArchived(context) && excludeArchivedContexts) {
        return;
      }

      const usage = {
        atomVersion: atomRelation.atomVersion,
        branchId: context.definition.authoringCourseBranchId,
        branchName: atomRelation.branchName,
        courseId: atomRelation.courseId,
        courseName: atomRelation.courseName,
        courseSlug: atomRelation.courseSlug,
        itemId: atomRelation.itemId,
        ...context,
      };

      const authoringCourseBranchId = usage.definition.authoringCourseBranchId;

      if (!usagesByBranchId[authoringCourseBranchId]) {
        usagesByBranchId[authoringCourseBranchId] = [];
      }

      if (isCurrentContext(usage, currentContextId)) {
        branchIdWithCurrentContext = authoringCourseBranchId;
        usagesByBranchId[authoringCourseBranchId].unshift(usage);
      } else {
        usagesByBranchId[authoringCourseBranchId].push(usage);
      }

      enrollmentsCount += getEnrollmentOrMembersCount(usage);
      sessionsCount += 1;
    });
  });

  // We assign all branches to a course with sorted contexts (status -> recent startDate)
  Object.values(usagesByBranchId).forEach((relatedUsage) => {
    const sortedContexts = sortContexts(relatedUsage, currentContextId);
    for (const context of sortedContexts) {
      usagesByCourseAndBranchId[context.courseId] = {
        ...usagesByCourseAndBranchId[context.courseId],
        [context.branchId]: sortedContexts,
      };
    }
  });

  const relatedUsagesData = {
    totalEnrollments: enrollmentsCount,
    totalSessions: sessionsCount,
    usagesByBranchId,
  };

  // If for some reason the current branch isn't here. Return the usages as is
  // This has been fixed in: https://coursera.atlassian.net/browse/COREAUTH-3740. Should be able to remove it.
  if (!branchIdWithCurrentContext) {
    return {
      ...relatedUsagesData,
      usagesByCourseAndBranchId,
    };
  }

  // Make sure the current course is always at the top
  const currentCourse = { ...usagesByCourseAndBranchId[currentCourseId] };
  delete usagesByCourseAndBranchId[currentCourseId];

  // Make sure the branch with the current context is at the top.
  const copyBranch = [...currentCourse[branchIdWithCurrentContext]];
  delete currentCourse[branchIdWithCurrentContext];

  return {
    ...relatedUsagesData,
    usagesByCourseAndBranchId: {
      [currentCourseId]: {
        [branchIdWithCurrentContext]: copyBranch,
        ...currentCourse,
      },
      ...usagesByCourseAndBranchId,
    },
  };
};

export const buildRelatedUsages = (
  itemContentRelations: ItemContentRelations,
  course: AuthoringCourse,
  branch: AuthoringCourseBranch,
  itemId: string,
  excludeCurrentUsage = false
): { relatedUsages: Array<RelatedUsage>; relatedUsagesByCourseId: { [courseId: string]: Array<RelatedUsage> } } => {
  const relatedUsages: Array<RelatedUsage> = []; // TODO: move to es6 model and flow type
  const relatedUsagesByCourseId: { [courseId: string]: Array<RelatedUsage> } = {};

  Object.keys(itemContentRelations.branchRelationMap).forEach((branchItemId: string) => {
    const atomRelation = itemContentRelations.branchRelationMap[branchItemId];
    const usage = {
      branchId: getBranchIdFromBranchItemId(branchItemId),
      itemId: getItemIdFromBranchItemId(branchItemId),
      ...atomRelation,
    };
    const shouldExcludeCurrentUsage = excludeCurrentUsage && usage.branchId === branch.id && usage.itemId === itemId;
    if (!shouldExcludeCurrentUsage) {
      relatedUsages.push(usage);

      if (typeof relatedUsagesByCourseId[atomRelation.courseId] === 'undefined') {
        relatedUsagesByCourseId[atomRelation.courseId] = [];
      }

      relatedUsagesByCourseId[atomRelation.courseId].push(usage);
    }
  });

  // current item will not exist in `itemContentRelations` for unlaunched course
  if (!excludeCurrentUsage && shouldAddCurrentUsage(itemContentRelations, branch.id, itemId)) {
    // current usage is displayed as the first item in the list
    const currentUsage = {
      courseName: course.name,
      courseSlug: course.slug,
      branchName: branch.name,
      branchId: branch.id,
      itemId,
      courseId: course.id,
      branchStatus: 'Draft', // any usages not in `itemContentRelations` are in draft status
    };

    relatedUsages.unshift(currentUsage);

    if (typeof relatedUsagesByCourseId[course.id] === 'undefined') {
      relatedUsagesByCourseId[course.id] = [];
    }

    relatedUsagesByCourseId[course.id].push(currentUsage);
  }

  return {
    relatedUsages,
    relatedUsagesByCourseId,
  };
};
